import React, { useState } from "react";
import {
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
  Toggle,
} from "@amzn/awsui-components-console";
import {
  DealStatus,
  FixedFeeConfig,
  SimplifiedFixedFeeConfig,
} from "@amzn/fuse-accounting-central-datastore-client";

const NOT_DRAFT_MESSAGE = "no supported for config not in DRAFT status";

interface EnableActionProps {
  fixedFeeConfig?: FixedFeeConfig | SimplifiedFixedFeeConfig;
  ldap: string;
  onEnable?: (input: {
    withEnhancedValidation: boolean;
    reason: string;
  }) => Promise<void> | void;
}

/**
 * Renders the "Enable" button plus its confirmation modal.
 *
 * - Only visible to users in the "bart-admin" LDAP group.
 * - Enabled only when the selected config is in DRAFT status.
 * - Clicking opens a confirmation modal that captures the "enhanced
 *   validation" toggle and an enablement reason. The actual enable action is
 *   not yet wired up; the captured values are ready for it.
 */
export const EnableAction: React.FC<EnableActionProps> = ({
  fixedFeeConfig,
  ldap,
  onEnable,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [withEnhancedValidation, setWithEnhancedValidation] = useState(true);
  const [reason, setReason] = useState("");

  if (!ldap.includes("bart-admin")) return null;

  const isDraft = fixedFeeConfig?.dealStatus === DealStatus.DRAFT;
  const isEnableDisabled = reason.trim().length === 0;

  const openModal = () => {
    // Reset the form each time the modal is opened.
    setWithEnhancedValidation(true);
    setReason("");
    setModalVisible(true);
  };

  const closeModal = () => setModalVisible(false);

  const onConfirmEnable = async () => {
    try {
      await onEnable?.({ withEnhancedValidation, reason });
    } finally {
      closeModal();
    }
  };

  const enableButton = (
    <Button
      variant="normal"
      iconName="check"
      disabled={!isDraft}
      onClick={openModal}
    >
      Enable
    </Button>
  );

  return (
    <>
      {fixedFeeConfig && !isDraft ? (
        <span title={NOT_DRAFT_MESSAGE}>{enableButton}</span>
      ) : (
        enableButton
      )}
      <Modal
        visible={modalVisible}
        onDismiss={closeModal}
        header="Enable Configuration"
        closeAriaLabel="Close dialog"
        footer={
          <Box float="right">
            <SpaceBetween size="xs" direction="horizontal">
              <Button variant="normal" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={isEnableDisabled}
                onClick={onConfirmEnable}
              >
                Enable
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <div>
            Are you sure you want to enable this configuration?{" "}
            <strong>This action is irreversible!</strong>
          </div>
          <Toggle
            checked={withEnhancedValidation}
            onChange={({ detail }) => setWithEnhancedValidation(detail.checked)}
            description="Validate things such as plan/ARD consistency, always applies for enablement through Partner Portal. Only toggle off if this is an exceptional case"
          >
            with enhanced validation
          </Toggle>
          <FormField
            label="Enablement approval/reason"
            description="Why you are enabling this configuration, SIM/TT/MCM link"
          >
            <Input
              value={reason}
              onChange={({ detail }) => setReason(detail.value)}
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </>
  );
};
